import { useEffect, useCallback } from 'react';
import { ReactFlowInstance } from 'reactflow';
import useStore from '../../store/useStore';

export const useUndoRedo = () => {
    // Select state and actions from Zustand store
    const { undo, redo, takeSnapshot, setReactFlowInstance } = useStore(
        (state) => ({
            undo: state.undo,
            redo: state.redo,
            past: state.past,
            future: state.future,
            takeSnapshot: state.takeSnapshot,
            setReactFlowInstance: state.setReactFlowInstance,
        }),
        // Shallow compare for performance
        (oldState, newState) =>
            oldState.undo === newState.undo &&
            oldState.redo === newState.redo &&
            oldState.past.length === newState.past.length &&
            oldState.future.length === newState.future.length &&
            oldState.takeSnapshot === newState.takeSnapshot &&
            oldState.setReactFlowInstance === newState.setReactFlowInstance
    );
    
    // Get canUndo and canRedo directly from the store's selectors
    // This ensures we are always using the most up-to-date computed value from the store itself.
    const canUndo = useStore(state => state.canUndo());
    const canRedo = useStore(state => state.canRedo());

    // Store React Flow instance in Zustand store
    const onInit = useCallback((instance: ReactFlowInstance) => {
        setReactFlowInstance(instance);
    }, [setReactFlowInstance]);

    // Keyboard shortcuts for Undo/Redo
    useEffect(() => {
        const handleKeyDown = (event: KeyboardEvent) => {
            // If the event is already handled by a deeper component (e.g., Lexical's HistoryPlugin), don't process it.
            if (event.defaultPrevented) {
                return;
            }

            const target = event.target as HTMLElement;
            // Ignore if typing in an input/textarea or contenteditable element
            // This check is crucial for allowing Lexical (or other text inputs) to handle their own undo/redo.
            if (target.isContentEditable || target.tagName === 'INPUT' || target.tagName === 'TEXTAREA') {
                return;
            }

            const isUndoEvent = (event.ctrlKey || event.metaKey) && event.key === 'z' && !event.shiftKey;
            const isRedoEvent = (event.ctrlKey || event.metaKey) && event.key === 'z' && event.shiftKey;

            if (isUndoEvent) {
                // Use the canUndo derived directly from the store for the condition
                const storeCanUndo = useStore.getState().canUndo();
                
                if (storeCanUndo) {
                    event.preventDefault();
                    
                    // Store state before undo for comparison
                    const beforeNodes = useStore.getState().nodes;
                    const beforeNodePositionsString = JSON.stringify(beforeNodes.map(n => ({id: n.id, pos: n.position})));
                    
                    // Call undo and capture the result
                    undo(); // This is the undo function from the store
                    
                    // Check state after undo
                    setTimeout(() => {
                        const afterNodes = useStore.getState().nodes;
                        const afterNodePositionsString = JSON.stringify(afterNodes.map(n => ({id: n.id, pos: n.position})));
                        
                        // Check if node positions actually changed by comparing the stringified positions of all nodes
                        const positionsChanged = beforeNodePositionsString !== afterNodePositionsString;
                        if (!positionsChanged && beforeNodes.length > 0 && afterNodes.length > 0) {
                        }
                    }, 0);
                } else {
                }
            } else if (isRedoEvent) {
                const storeCanRedo = useStore.getState().canRedo();
                
                if (storeCanRedo) {
                    event.preventDefault();
                    
                    // Store state before redo for comparison
                    const beforeNodes = useStore.getState().nodes;
                    const beforeNodePositionsString = JSON.stringify(beforeNodes.map(n => ({id: n.id, pos: n.position})));
                    
                    // Call redo and capture the result
                    redo(); // This is the redo function from the store
                    
                    // Check state after redo
                    setTimeout(() => {
                        const afterNodes = useStore.getState().nodes;
                        const afterNodePositionsString = JSON.stringify(afterNodes.map(n => ({id: n.id, pos: n.position})));
                        
                        // Check if node positions actually changed by comparing the stringified positions of all nodes
                        const positionsChanged = beforeNodePositionsString !== afterNodePositionsString;
                         if (!positionsChanged && beforeNodes.length > 0 && afterNodes.length > 0) {
                        }
                    }, 0);
                } else {
                }
            }
        };

        document.addEventListener('keydown', handleKeyDown);
        return () => {
            document.removeEventListener('keydown', handleKeyDown);
        };
    }, [undo, redo]); // Removed canUndo, canRedo from dependencies as they are now read directly from store inside handler

    return {
        undo, // from store
        redo, // from store
        canUndo, // reactive value for UI
        canRedo, // reactive value for UI
        takeSnapshot, // from store
        onInit, // Expose onInit to be used by ReactFlow
    };
};
